/**
 * Pixel Button — pixel-faithful port of `design/Pixel Button.dc.html`
 * (DC-RUNTIME §6.3).
 *
 * Contract notes for the screen lanes:
 *  - **Glyph parsing is exact and label-driven.** A label ending in `' ▸'`
 *    (space + glyph) renders the 4-rect pixel triangle rotated 90°; `' ✕'`
 *    renders a 1.4em ✕ span. The text is `label.slice(0, -2)` — the leading
 *    space is required, and no other glyph is recognised. Do not hand-roll an
 *    `icon` prop; pass `label="START ▸"`.
 *  - It is a `<div onClick>`, not a `<button>` — a faithful port of the frozen
 *    design (DC-RUNTIME §5). Keep it that way unless a11y work is scoped.
 *  - `fs` may arrive as a string; the arrow is sized `round(fs*1.2)` ×
 *    `round(fs*1.1)`.
 *
 * Prototype call sites (INVENTORY §11):
 *   login   `label="START ▸" fs={9} pad="15px 14px" nowrap`
 *   reset   `label="SET PASSWORD & ENTER ▸" fs={8.5} pad="15px 14px" center`
 *   detail  `label="POST ▸" fs={8} pad="14px 14px" center`
 *   admin   `label="+ ADD USER" fs={8} pad="14px 14px" nowrap`
 *   profile `label="SAVE CHANGES" | "UPDATE PASSWORD" fs={8} pad="14px 14px" center`
 *   signout `label="SIGN OUT ✕" variant="outline" tone="acc2" fs={8} pad="13px 14px" center shadow={false}`
 *   pt add  `label="+ TRACK FOR EVERYONE" fs={8} pad="14px 10px" center lh="1" shadow={false}`
 */

import type { CSSProperties } from 'react';

import { PixelTriangle } from './PixelTriangle.tsx';

export interface PixelButtonProps {
  /** DC default `'BUTTON'`; trailing `' ▸'` / `' ✕'` become glyphs */
  label?: string;
  variant?: 'solid' | 'outline';
  tone?: 'acc' | 'acc2';
  /** pixel-font px — DC default 9 */
  fs?: number | string;
  /** DC default `'14px 16px'` */
  pad?: string;
  /** `--gt-shadow-btn` — DC default true */
  shadow?: boolean;
  center?: boolean;
  nowrap?: boolean;
  /** `width:fit-content` instead of `auto` */
  fit?: boolean;
  /** DC default `'normal'` */
  lh?: number | string;
  onClick?: () => void;
  /** host-wrapper style — positional properties only */
  style?: CSSProperties;
  /**
   * A write this button started is still open.
   *
   * Dims to the muted tone, switches the cursor to `progress`, and SWALLOWS the
   * click — so the store guard is visible at the leaf as well as enforced in the
   * slice. The label stays the caller's: pass the busy string down from the
   * view-model, so both trees say the same word.
   *
   * The three values are the mirrors' own busy recipe, not invented here —
   * `design/Games Tonight.dc.html` lines 1122-1125 (`cBd` / `cClr` / `cCur`),
   * which the app already ports for the GOTY confirm in `gotyPickVM`. What IS an
   * extension is having it on the primitive at all: `design/Pixel Button.dc.html`
   * line 18 hardcodes `cursor:pointer`, so this owes an upstream design edit and
   * a DESIGN-PARITY row.
   *
   * NEVER pass a busy label ending in `' ▸'`. The glyph is parsed off the last
   * two characters, and the arrow means "go" — it has no business being on
   * screen while the thing is already going.
   *
   * **NOT A GENERAL "DISABLED".** Use `disabled` for that — see below.
   */
  busy?: boolean;
  /**
   * There is nothing for this button to do yet.
   *
   * Looks exactly like `busy` and swallows the click the same way, but leaves
   * the cursor ALONE. That single difference is the whole reason it exists:
   * `busy` sets `cursor: progress`, which promises that something is happening.
   *
   * RENAME shipped using `busy` for "the name has not changed", which is its
   * RESTING state — so the crew screen showed a loading cursor permanently, on
   * a button that was not doing anything (Marcus, 2026-08-21). A control that
   * cannot act yet is not a control that is working.
   */
  disabled?: boolean;
}

export function PixelButton({
  label,
  variant,
  tone,
  fs,
  pad,
  shadow,
  center,
  nowrap,
  fit,
  lh,
  onClick,
  style,
  busy,
  disabled,
}: PixelButtonProps) {
  const v = variant ?? 'solid';
  const accV = (tone ?? 'acc') === 'acc2' ? 'var(--acc2)' : 'var(--acc)';
  const isBusy = busy ?? false;
  const isOff = disabled ?? false;
  // The two share every visual except the cursor, which is the only thing that
  // distinguishes "working" from "nothing to do" — see `disabled`.
  const dim = isBusy || isOff;
  const raw = label ?? 'BUTTON';
  const glyph = raw.endsWith(' ▸') ? '▸' : raw.endsWith(' ✕') ? '✕' : null;
  const fsRaw = fs ?? 9;
  const fsNum = Number(fsRaw);
  const isCenter = center ?? false;

  return (
    <div className="sc-host" data-sc-name="Pixel Button" style={style}>
      <div
        onClick={dim ? () => {} : (onClick ?? (() => {}))}
        style={{
          background: v === 'solid' ? (dim ? 'var(--gt-border-2)' : accV) : 'transparent',
          color: dim ? 'var(--gt-muted)' : v === 'solid' ? 'var(--gt-ink)' : accV,
          border: v === 'solid' ? '0' : '1px solid ' + (dim ? 'var(--gt-border-2)' : accV),
          fontFamily: 'var(--gt-font-pixel)',
          fontSize: fsRaw + 'px',
          padding: pad ?? '14px 16px',
          // `progress` ONLY while something is genuinely in flight. A disabled
          // control gets the ordinary arrow: it is not working, it is waiting
          // for you.
          cursor: isBusy ? 'progress' : isOff ? 'default' : 'pointer',
          boxShadow: (shadow ?? true) ? 'var(--gt-shadow-btn)' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCenter ? 'center' : 'flex-start',
          gap: '0.9em',
          textAlign: isCenter ? 'center' : 'left',
          whiteSpace: (nowrap ?? false) ? 'nowrap' : 'normal',
          width: (fit ?? false) ? 'fit-content' : 'auto',
          lineHeight: '' + (lh ?? 'normal'),
          boxSizing: 'border-box',
        }}
      >
        <span style={{ transform: 'translateY(0.13em)' }}>
          <span>{glyph ? raw.slice(0, -2) : raw}</span>
        </span>
        {glyph === '▸' ? (
          <PixelTriangle w={Math.round(fsNum * 1.2)} h={Math.round(fsNum * 1.1)} rotate={90} />
        ) : null}
        {glyph === '✕' ? <span style={{ fontSize: '1.4em', lineHeight: 1 }}>✕</span> : null}
      </div>
    </div>
  );
}
